"use client";

import {
  useState,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  CalendarClock,
  CreditCard,
  MessageCircleHeart,
  UserPen,
} from "lucide-react";
import {
  ServiceSelection,
  Service,
  BookingStepper,
  BookingStep,
  BookingNavigation,
  SupportOptions,
  SupportOption,
  ServiceLocation,
  LocationOption,
  UserLocationData,
  DateTimePickerFirst,
  DateTimeSelectionFirst,
  PractitionerSelectionFiltered,
  PractitionerFiltered,
  PersonalDetails,
  PersonalDetailsData,
  Payment,
  BookingConfirmation,
  FundingSupport,
  FundingSupportData,
  OtEnquiryForm,
  OtEnquiryFormData,
  OtEnquiryConfirmation,
  ServiceTheme,
} from "@/components/v3/booking";
import { useCreateOtEnquiryMutation } from "@/redux/features/otEnquiry/otEnquiryApi";
import { useCreateAppointmentMutation } from "@/redux/features/appointment/appointmentApi";
import { useGetDivisionsQuery } from "@/redux/features/divisions/divisionsApi";
import {
  SessionType,
  ServiceDeliveryMedium,
  TDivision,
  DivisionType,
} from "@/types";
import { useSelectedTimezone } from "@/components/v3/booking/TimezoneSelector";
import { trackPaymentCompleted, trackOtEnquirySubmitted } from "@/lib/analytics";

/**
 * Condensed 4-step booking flow.
 *
 * The original /booking flow spreads the same work over six stepper steps.
 * Here the related decisions are grouped so the client only sees four:
 *
 *   01 Your Needs   - support option (+ funding support) + service location
 *   02 Schedule     - date & time + practitioner for that slot
 *   03 Your Details - personal details
 *   04 Payment      - payment
 *
 * Internal step indices: 0 = service selection (no stepper), 1-4 = the steps
 * above, 5 = confirmation. Occupational Therapy keeps its enquiry-only flow.
 */

// URL param to service ID mapping
const serviceParamMapping: Record<
  string,
  { id: string; theme: Service["theme"]; divisionType: DivisionType }
> = {
  counselling: {
    id: "counseling",
    theme: "counseling",
    divisionType: "COUNSELLING",
  },
  counseling: {
    id: "counseling",
    theme: "counseling",
    divisionType: "COUNSELLING",
  },
  "life-coach": {
    id: "life-coaching",
    theme: "lifeCoaching",
    divisionType: "LIFE_COACHING",
  },
  "life-coaching": {
    id: "life-coaching",
    theme: "lifeCoaching",
    divisionType: "LIFE_COACHING",
  },
  "occupational-therapy": {
    id: "occupational-therapy",
    theme: "occupationalTherapy",
    divisionType: "OCCUPATIONAL_THERAPY",
  },
  ot: {
    id: "occupational-therapy",
    theme: "occupationalTherapy",
    divisionType: "OCCUPATIONAL_THERAPY",
  },
  psychology: {
    id: "psychology",
    theme: "psychology",
    divisionType: "PSYCHOLOGY",
  },
};

// Service display names for dynamic header
const serviceDisplayNames: Record<string, string> = {
  counseling: "Counselling",
  "life-coaching": "Life Coaching",
  "occupational-therapy": "Occupational Therapy",
  psychology: "Psychology",
};

// The four grouped stepper steps (service selection sits before the stepper)
const groupedSteps: BookingStep[] = [
  {
    id: "your-needs",
    number: "01",
    label: "Your Needs",
    icon: <MessageCircleHeart className="size-5" />,
  },
  {
    id: "schedule",
    number: "02",
    label: "Schedule",
    icon: <CalendarClock className="size-5" />,
  },
  {
    id: "your-details",
    number: "03",
    label: "Your Details",
    icon: <UserPen className="size-5" />,
  },
  {
    id: "payment",
    number: "04",
    label: "Payment",
    icon: <CreditCard className="size-5" />,
  },
];

const TOTAL_STEPS = groupedSteps.length;

// Internal step indices
const STEP_SERVICE = 0;
const STEP_NEEDS = 1;
const STEP_SCHEDULE = 2;
const STEP_DETAILS = 3;
const STEP_PAYMENT = 4;
const STEP_CONFIRMATION = 5;

// Email OTP verification gate: clients must verify their email with an OTP
// before proceeding to payment or submitting an OT enquiry.
// Set to false if you ever need to temporarily bypass verification.
const REQUIRE_EMAIL_VERIFICATION = true;

// Booking state interface
interface BookingState {
  service: Service | null;
  divisionId: string | null; // Backend division ID
  supportOption: SupportOption | null;
  fundingSupport: FundingSupportData | null;
  location: LocationOption | null;
  userLocation: UserLocationData | null;
  dateTimeFirst: DateTimeSelectionFirst | null; // Date-first flow
  practitionerFiltered: PractitionerFiltered | null; // Date-first flow
  personalDetails: PersonalDetailsData | null;
  appointmentId: string | null; // Created appointment ID
  otEnquiry: OtEnquiryFormData | null; // For OT enquiry-only flow
  otEnquiryId: string | null; // Created OT enquiry ID
}

const initialBookingState: BookingState = {
  service: null,
  divisionId: null,
  supportOption: null,
  fundingSupport: null,
  location: null,
  userLocation: null,
  dateTimeFirst: null,
  practitionerFiltered: null,
  personalDetails: null,
  appointmentId: null,
  otEnquiry: null,
  otEnquiryId: null,
};

// Helper function to check if service is OT (requires enquiry-only flow)
const isOtService = (service: Service | null): boolean => {
  if (!service) return false;
  return service.id === "occupational-therapy";
};

const isNdisEligibleService = (_service: Service | null): boolean => {
  return false;
};

// Helper function to get service theme for BookingStepper brand colors
const getServiceTheme = (service: Service | null): ServiceTheme | undefined => {
  if (!service) return undefined;
  const themeMap: Record<Service["theme"], ServiceTheme> = {
    counseling: "counseling",
    lifeCoaching: "lifeCoaching",
    occupationalTherapy: "occupationalTherapy",
    psychology: "psychology",
  };
  return themeMap[service.theme];
};

// Card border colour: red for OT/NDIS-eligible services, brand blue otherwise
const getAccentColor = (service: Service | null): string =>
  isOtService(service) || isNdisEligibleService(service) ? "#C01610" : "#006DFF";

// Page header component with dynamic step count
interface BookingHeaderProps {
  stepCount?: number;
}

const BookingHeader = ({ stepCount = TOTAL_STEPS }: BookingHeaderProps) => (
  <header className="w-full">
    <h1 className="font-crimsonText text-3xl font-bold leading-tight text-v3-secondary sm:text-4xl md:text-4xl lg:text-[44px] lg:leading-none">
      Book Your Session in {stepCount} steps
    </h1>
  </header>
);

// One content block inside a step card; grouped steps stack several of these
const StepSection = ({
  children,
  divider = false,
  scrollRef,
  overflowX = false,
}: {
  children: ReactNode;
  divider?: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
  overflowX?: boolean;
}) => (
  <>
    {divider && <div className="mx-4 border-t border-gray-200 md:mx-[30px]" />}
    <div
      ref={scrollRef}
      className={`scroll-mt-28 bg-white p-4 md:p-[30px]${overflowX ? " overflow-x-auto" : ""}`}
    >
      {children}
    </div>
  </>
);

// Shared frame for every step: header, card, stepper, sections, navigation
const StepFrame = ({
  accentColor,
  serviceTheme,
  stepperIndex = 0,
  allCompleted = false,
  showStepper = true,
  onStepClick,
  children,
  footer,
}: {
  accentColor: string;
  serviceTheme?: ServiceTheme;
  stepperIndex?: number;
  allCompleted?: boolean;
  showStepper?: boolean;
  onStepClick?: (stepIndex: number) => void;
  children: ReactNode;
  footer: ReactNode;
}) => (
  <>
    <BookingHeader />
    <article
      className="w-full overflow-hidden rounded-[10px] border bg-white"
      style={{ borderColor: accentColor }}
    >
      {showStepper && (
        <StepSection overflowX>
          <BookingStepper
            steps={groupedSteps}
            currentStep={stepperIndex}
            allCompleted={allCompleted}
            serviceTheme={serviceTheme}
            onStepClick={onStepClick}
          />
        </StepSection>
      )}
      {children}
      <StepSection>{footer}</StepSection>
    </article>
  </>
);

// Inner component that uses useSearchParams (must be wrapped in Suspense)
function BookingPageContent() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [currentStep, setCurrentStep] = useState(STEP_SERVICE);
  const [urlParamProcessed, setUrlParamProcessed] = useState(false);
  const [bookingState, setBookingState] =
    useState<BookingState>(initialBookingState);

  // Refs for the sub-sections revealed inside a grouped step
  const locationSectionRef = useRef<HTMLDivElement>(null);
  const practitionerSectionRef = useRef<HTMLDivElement>(null);

  // Fetch divisions to get divisionId for URL param service
  const { data: divisionsResponse } = useGetDivisionsQuery({});

  // Appointment creation mutation
  const [createAppointment, { isLoading: isCreatingAppointment }] =
    useCreateAppointmentMutation();

  // OT Enquiry creation mutation
  const [createOtEnquiry, { isLoading: isCreatingOtEnquiry }] =
    useCreateOtEnquiryMutation();

  // Get user's effective timezone for booking
  const { effectiveTimezone } = useSelectedTimezone();

  // Check if current service is OT (requires enquiry-only flow)
  const isOtFlow = isOtService(bookingState.service);

  // Check if current service is NDIS-eligible (funding support inside step 1)
  const hasNdisStep = isNdisEligibleService(bookingState.service);

  const serviceTheme = getServiceTheme(bookingState.service);
  const accentColor = getAccentColor(bookingState.service);

  // Scroll to top whenever the step changes (next, back, or stepper click)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // Handle URL query param for pre-selected service
  useEffect(() => {
    const resetBooking = searchParams.get("reset");
    if (resetBooking) {
      setBookingState(initialBookingState);
      setCurrentStep(STEP_SERVICE);
      setUrlParamProcessed(true);
      router.replace(pathname, { scroll: false });
      return;
    }

    if (urlParamProcessed) return;

    const serviceParam = searchParams.get("service");
    if (!serviceParam) {
      setUrlParamProcessed(true);
      return;
    }

    const mappedService = serviceParamMapping[serviceParam.toLowerCase()];
    if (!mappedService) {
      setUrlParamProcessed(true);
      return;
    }

    // Wait for divisions data to get the divisionId
    if (!divisionsResponse?.data) return;

    const divisions = divisionsResponse.data as TDivision[];
    const matchingDivision = divisions.find(
      (d) => d.type === mappedService.divisionType && d.is_active,
    );

    if (matchingDivision) {
      const preSelectedService: Service = {
        id: mappedService.id,
        name: serviceDisplayNames[mappedService.id] || mappedService.id,
        description: matchingDivision.description || "",
        theme: mappedService.theme,
        divisionId: matchingDivision.id,
        divisionType: matchingDivision.type,
      };

      setBookingState((prev) => ({
        ...prev,
        service: preSelectedService,
        divisionId: matchingDivision.id,
      }));
      setCurrentStep(STEP_NEEDS);
    }

    setUrlParamProcessed(true);
  }, [searchParams, divisionsResponse, pathname, router, urlParamProcessed]);

  // OT is enquiry-only (form + confirmation); everything else uses the 4 steps
  const getStepCount = (): number => (isOtFlow ? 2 : TOTAL_STEPS);

  // Get service display name for header
  const getServiceDisplayName = (): string => {
    if (!bookingState.service) return "Counselling";
    return (
      serviceDisplayNames[bookingState.service.id] || bookingState.service.name
    );
  };

  // Step 0: Service Selection
  const serviceIdToUrlParam: Record<string, string> = {
    counseling: "counselling",
    "life-coaching": "life-coaching",
    "occupational-therapy": "occupational-therapy",
    psychology: "psychology",
  };

  const updateServiceParamInUrl = useCallback(
    (service: Service) => {
      const nextParams = new URLSearchParams(searchParams.toString());
      const serviceParam = serviceIdToUrlParam[service.id];

      if (!serviceParam) return;

      nextParams.set("service", serviceParam);
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;

      router.replace(nextUrl, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  const handleServiceSelect = (service: Service) => {
    setBookingState((prev) => ({
      ...prev,
      service,
      divisionId: service.divisionId || null,
    }));
  };

  const handleServiceContinue = (selectedService: Service | null) => {
    if (selectedService) {
      setUrlParamProcessed(true);
      updateServiceParamInUrl(selectedService);
      setBookingState((prev) => ({
        ...prev,
        service: selectedService,
        divisionId: selectedService.divisionId || null,
      }));
      setCurrentStep(STEP_NEEDS);
    }
  };

  // Scroll a revealed sub-section into view once it has rendered
  const revealSection = (ref: React.RefObject<HTMLDivElement>) => {
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Step 1: Support Options
  const handleSupportOptionSelect = (option: SupportOption) => {
    const isFirstSelection = !bookingState.supportOption;
    setBookingState((prev) => {
      // A different service means the previously picked slot may no longer exist
      const sameService = prev.supportOption?.serviceId === option.serviceId;
      return {
        ...prev,
        supportOption: option,
        dateTimeFirst: sameService ? prev.dateTimeFirst : null,
        practitionerFiltered: sameService ? prev.practitionerFiltered : null,
      };
    });
    if (isFirstSelection) revealSection(locationSectionRef);
  };

  // Step 1 (NDIS-eligible services only): Funding Support
  const handleFundingSupportChange = (data: FundingSupportData) => {
    setBookingState((prev) => ({ ...prev, fundingSupport: data }));
  };

  // OT Enquiry form data change
  const handleOtEnquiryChange = (data: OtEnquiryFormData) => {
    setBookingState((prev) => ({ ...prev, otEnquiry: data }));
  };

  // Submit OT Enquiry
  const handleOtEnquirySubmit = async () => {
    const { otEnquiry } = bookingState;

    if (!otEnquiry) {
      console.error("Missing OT enquiry data");
      return;
    }

    try {
      const result = await createOtEnquiry({
        firstName: otEnquiry.firstName,
        lastName: otEnquiry.lastName,
        email: otEnquiry.email,
        phone: otEnquiry.phone,
        dateOfBirth: otEnquiry.dateOfBirth || undefined,
        address: otEnquiry.address || undefined,
        preferredDate: otEnquiry.preferredDate || undefined,
        reasonForEnquiry: otEnquiry.reasonForEnquiry || undefined,
      }).unwrap();

      if (result?.data?.id) {
        trackOtEnquirySubmitted({
          enquiryId: result.data.id,
          service: "occupational-therapy",
        });
        setBookingState((prev) => ({ ...prev, otEnquiryId: result.data.id }));
        handleContinue(); // Move to confirmation step
      }
    } catch (error) {
      console.error("Failed to submit OT enquiry:", error);
    }
  };

  // Step 1: Service Location
  const handleLocationSelect = (option: LocationOption) => {
    setBookingState((prev) => {
      // Session type drives slot availability, so a changed location
      // invalidates any slot and practitioner already chosen in step 2
      const sameLocation = prev.location?.id === option.id;
      return {
        ...prev,
        location: option,
        dateTimeFirst: sameLocation ? prev.dateTimeFirst : null,
        practitionerFiltered: sameLocation ? prev.practitionerFiltered : null,
      };
    });
  };

  const handleLocationAddressChange = (location: UserLocationData) => {
    setBookingState((prev) => ({ ...prev, userLocation: location }));
  };

  // Step 2: Date & Time selection
  const handleDateTimeFirstChange = (selection: DateTimeSelectionFirst) => {
    setBookingState((prev) => {
      const slotChanged =
        prev.dateTimeFirst?.startTime !== selection.startTime ||
        prev.dateTimeFirst?.date?.getTime() !== selection.date?.getTime();
      return {
        ...prev,
        dateTimeFirst: selection,
        // Practitioner availability is slot-specific
        practitionerFiltered: slotChanged ? null : prev.practitionerFiltered,
      };
    });
    if (selection.date && selection.startTime) {
      revealSection(practitionerSectionRef);
    }
  };

  // Step 2: Practitioner selection (filtered by slot)
  const handlePractitionerFilteredSelect = (
    practitioner: PractitionerFiltered,
  ) => {
    setBookingState((prev) => ({
      ...prev,
      practitionerFiltered: practitioner,
    }));
  };

  // Step 3: Personal Details
  const handlePersonalDetailsChange = (details: PersonalDetailsData) => {
    setBookingState((prev) => ({ ...prev, personalDetails: details }));
  };

  // Helper to get session type for backend
  const getSessionType = (): SessionType => {
    if (bookingState.location?.id === "online") return "ONLINE";
    return "IN_PERSON";
  };

  // Helper to get service delivery medium for backend
  const getServiceDeliveryMedium = (): ServiceDeliveryMedium | undefined => {
    const locationId = bookingState.location?.id;
    if (locationId === "at-my-location") return "HOME_VISIT";
    if (locationId === "in-clinic" || locationId === "in-person")
      return "CLINIC";
    if (locationId === "online") return "TELEHEALTH";
    return undefined;
  };

  // Create appointment before payment
  const handleCreateAppointment = useCallback(async () => {
    const {
      personalDetails,
      dateTimeFirst,
      supportOption,
      practitionerFiltered,
      userLocation,
      fundingSupport,
    } = bookingState;

    if (
      !personalDetails ||
      !dateTimeFirst?.date ||
      !practitionerFiltered?.slotId ||
      !supportOption?.serviceId
    ) {
      console.error("Missing required booking data", {
        hasPersonalDetails: !!personalDetails,
        hasDate: !!dateTimeFirst?.date,
        hasSlotId: !!practitionerFiltered?.slotId,
        hasServiceId: !!supportOption?.serviceId,
      });
      return null;
    }

    const appointmentData = {
      firstName: personalDetails.firstName,
      lastName: personalDetails.lastName,
      email: personalDetails.email,
      phone: personalDetails.phone,
      dateOfBirth: personalDetails.dateOfBirth,
      gender: personalDetails.gender?.toUpperCase() as
        | "MALE"
        | "FEMALE"
        | "OTHER",
      sessionType: getSessionType(),
      date:
        dateTimeFirst.date instanceof Date
          ? `${dateTimeFirst.date.getFullYear()}-${String(dateTimeFirst.date.getMonth() + 1).padStart(2, "0")}-${String(dateTimeFirst.date.getDate()).padStart(2, "0")}`
          : dateTimeFirst.date,
      timeSlotId: practitionerFiltered.slotId,
      serviceId: supportOption.serviceId,
      counselorId: practitionerFiltered.counselorId,
      service_delivery_medium: getServiceDeliveryMedium(),
      delivery_address: userLocation?.address,
      has_ndis_medicare:
        fundingSupport?.fundingType === "ndis" ||
        fundingSupport?.fundingType === "medicare",
      ndis_medicare_type:
        fundingSupport?.fundingType === "ndis"
          ? "NDIS"
          : fundingSupport?.fundingType === "medicare"
            ? "MEDICARE"
            : undefined,
      ndis_medicare_number: fundingSupport?.certificateNumber,
      notes: personalDetails.notes,
      client_timezone: effectiveTimezone,
    };

    try {
      const result = await createAppointment(appointmentData).unwrap();
      if (result?.data?.id) {
        setBookingState((prev) => ({ ...prev, appointmentId: result.data.id }));
        return result.data.id;
      }
      return null;
    } catch (error) {
      console.error("Failed to create appointment:", error);
      return null;
    }
  }, [bookingState, createAppointment, effectiveTimezone]);

  // Step 4: Payment callbacks (status-only logs, no sensitive payloads)
  const handlePaymentSubmit = () => {
    console.log("Payment submit action triggered");
  };

  const handlePaymentSuccess = (paymentIntentId?: string) => {
    console.log("Payment succeeded");
    trackPaymentCompleted({
      source: "booking_flow",
      service: bookingState.service?.name || bookingState.service?.id,
      value: bookingState.supportOption?.price,
      transactionId: paymentIntentId,
    });
    // Move to confirmation step
    handleContinue();
  };

  const handlePaymentError = () => {
    console.error("Payment failed");
  };

  // Navigate to a completed stepper step (stepperIndex is 0-based)
  const handleStepperClick = (stepperIndex: number) => {
    // Internal step = stepperIndex + 1 (stepper starts at internal step 1)
    const targetStep = stepperIndex + 1;
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
    }
  };

  // Navigation
  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(STEP_SERVICE, prev - 1));
  };

  const handleContinue = () => {
    setCurrentStep((prev) => prev + 1);
  };

  // Handle continue from personal details to payment (create appointment first)
  const handlePersonalDetailsContinue = async () => {
    // Create appointment when moving to payment step
    const appointmentId = await handleCreateAppointment();
    if (appointmentId) {
      handleContinue();
    } else {
      // Show error to user (appointment creation failed)
      console.error("Failed to create appointment");
    }
  };

  // Check if funding support is valid (for continue button)
  const isFundingSupportValid = () => {
    const fs = bookingState.fundingSupport;
    if (!fs || !fs.fundingType) return false;
    if (fs.fundingType === "not-applicable") return true;
    // For Medicare/NDIS, certificate number is required
    if (!fs.certificateNumber || fs.certificateNumber.length < 3) return false;
    // Document method is optional but recommended
    return true;
  };

  // Booking summary passed down to the later steps
  const baseSummary = {
    sessionType:
      bookingState.location?.id === "online"
        ? ("online" as const)
        : ("in-person" as const),
    supportOption: bookingState.supportOption?.name || "Session",
    duration: bookingState.supportOption?.duration || "60 min",
    price: bookingState.supportOption?.price || 130,
  };

  const fullSummary = {
    ...baseSummary,
    appointmentDate: bookingState.dateTimeFirst?.date
      ? new Date(bookingState.dateTimeFirst.date)
      : undefined,
    appointmentTime: bookingState.dateTimeFirst?.startTime,
    practitionerName: bookingState.practitionerFiltered?.name,
    practitionerRole: bookingState.practitionerFiltered?.role,
    practitionerImage: bookingState.practitionerFiltered?.image,
  };

  // Step 1 is complete once the support option, funding (when required) and
  // location have all been chosen
  const isNeedsStepComplete =
    !!bookingState.supportOption &&
    !!bookingState.location &&
    (!hasNdisStep || isFundingSupportValid());

  const isScheduleStepComplete =
    !!bookingState.dateTimeFirst?.date &&
    !!bookingState.dateTimeFirst?.startTime &&
    !!bookingState.practitionerFiltered;

  const isDetailsStepComplete =
    !!bookingState.personalDetails?.firstName &&
    !!bookingState.personalDetails?.lastName &&
    !!bookingState.personalDetails?.gender &&
    !!bookingState.personalDetails?.dateOfBirth &&
    !!bookingState.personalDetails?.email &&
    !!bookingState.personalDetails?.phone &&
    (!REQUIRE_EMAIL_VERIFICATION ||
      !!bookingState.personalDetails?.emailVerified);

  // Render step content
  const renderStepContent = () => {
    // Step 0: Service Selection (same for all)
    if (currentStep === STEP_SERVICE) {
      return (
        <>
          <BookingHeader stepCount={getStepCount()} />

          {/* Service Selection Card */}
          <article className="w-full overflow-hidden rounded-[10px] border border-v3-primary bg-white p-4 md:p-[30px]">
            <ServiceSelection
              onServiceSelect={handleServiceSelect}
              onContinue={handleServiceContinue}
            />
          </article>
        </>
      );
    }

    // For OT service - enquiry-only flow (no booking, just enquiry form)
    if (isOtFlow) {
      if (currentStep === STEP_NEEDS) {
        return (
          <StepFrame
            accentColor={accentColor}
            serviceTheme={serviceTheme}
            showStepper={false}
            footer={
              <BookingNavigation
                onPrevious={handlePrevious}
                onContinue={handleOtEnquirySubmit}
                showPrevious={true}
                continueDisabled={
                  !bookingState.otEnquiry?.firstName ||
                  !bookingState.otEnquiry?.lastName ||
                  !bookingState.otEnquiry?.email ||
                  !bookingState.otEnquiry?.phone ||
                  (REQUIRE_EMAIL_VERIFICATION &&
                    !bookingState.otEnquiry?.emailVerified) ||
                  isCreatingOtEnquiry
                }
                continueLabel={
                  isCreatingOtEnquiry ? "Submitting..." : "Submit Enquiry"
                }
                serviceTheme={serviceTheme}
              />
            }
          >
            <StepSection>
              <OtEnquiryForm
                onDataChange={handleOtEnquiryChange}
                defaultData={bookingState.otEnquiry || undefined}
                divisionId={bookingState.divisionId || ""}
                requireEmailVerification={REQUIRE_EMAIL_VERIFICATION}
              />
            </StepSection>
          </StepFrame>
        );
      }

      // OT Enquiry Confirmation
      return (
        <StepFrame
          accentColor={accentColor}
          serviceTheme={serviceTheme}
          showStepper={false}
          footer={
            <BookingNavigation
              showPrevious={false}
              continueLabel="Back to Home"
              onContinue={() => {
                window.location.href = "/";
              }}
              serviceTheme={serviceTheme}
            />
          }
        >
          <StepSection>
            <OtEnquiryConfirmation
              enquiryId={bookingState.otEnquiryId || undefined}
              clientName={`${bookingState.otEnquiry?.firstName || ""} ${bookingState.otEnquiry?.lastName || ""}`}
              email={bookingState.otEnquiry?.email}
            />
          </StepSection>
        </StepFrame>
      );
    }

    switch (currentStep) {
      // Step 1: Your Needs - support option (+ funding) + service location
      case STEP_NEEDS:
        return (
          <StepFrame
            accentColor={accentColor}
            serviceTheme={serviceTheme}
            stepperIndex={0}
            onStepClick={handleStepperClick}
            footer={
              <BookingNavigation
                onPrevious={handlePrevious}
                onContinue={handleContinue}
                showPrevious={true}
                continueDisabled={!isNeedsStepComplete}
                serviceTheme={serviceTheme}
              />
            }
          >
            <StepSection>
              <SupportOptions
                onOptionSelect={handleSupportOptionSelect}
                defaultSelectedId={bookingState.supportOption?.id}
                divisionId={bookingState.divisionId || undefined}
                serviceTheme={serviceTheme}
                serviceName={getServiceDisplayName()}
              />
            </StepSection>

            {/* Funding support only applies to NDIS-eligible services */}
            {hasNdisStep && bookingState.supportOption && (
              <StepSection divider>
                <FundingSupport
                  onDataChange={handleFundingSupportChange}
                  defaultData={bookingState.fundingSupport || undefined}
                  appointmentId={bookingState.appointmentId || undefined}
                />
              </StepSection>
            )}

            {/* Location is revealed once a support option has been chosen */}
            {bookingState.supportOption && (
              <StepSection divider scrollRef={locationSectionRef}>
                <ServiceLocation
                  onOptionSelect={handleLocationSelect}
                  defaultSelectedId={bookingState.location?.id}
                  showAtMyLocation={hasNdisStep}
                  onLocationAddressChange={handleLocationAddressChange}
                  defaultLocationAddress={bookingState.userLocation?.address}
                  serviceTheme={serviceTheme}
                />
              </StepSection>
            )}
          </StepFrame>
        );

      // Step 2: Schedule - date & time + practitioner for the chosen slot
      case STEP_SCHEDULE:
        return (
          <StepFrame
            accentColor={accentColor}
            serviceTheme={serviceTheme}
            stepperIndex={1}
            onStepClick={handleStepperClick}
            footer={
              <BookingNavigation
                onPrevious={handlePrevious}
                onContinue={handleContinue}
                showPrevious={true}
                continueDisabled={!isScheduleStepComplete}
                serviceTheme={serviceTheme}
              />
            }
          >
            <StepSection>
              <DateTimePickerFirst
                onSelectionChange={handleDateTimeFirstChange}
                defaultSelection={bookingState.dateTimeFirst || undefined}
                divisionId={bookingState.divisionId || ""}
                serviceId={bookingState.supportOption?.serviceId}
                sessionType={getSessionType()}
                serviceTheme={serviceTheme}
                bookingSummary={baseSummary}
              />
            </StepSection>

            {/* Practitioners are slot-specific, so this waits for a time slot.
                No bookingSummary here - the picker above already shows one. */}
            {bookingState.dateTimeFirst?.date &&
              bookingState.dateTimeFirst?.startTime && (
                <StepSection divider scrollRef={practitionerSectionRef}>
                  <PractitionerSelectionFiltered
                    onPractitionerSelect={handlePractitionerFilteredSelect}
                    defaultSelectedId={bookingState.practitionerFiltered?.id}
                    divisionId={bookingState.divisionId || ""}
                    serviceId={bookingState.supportOption?.serviceId}
                    date={`${bookingState.dateTimeFirst.date.getFullYear()}-${String(bookingState.dateTimeFirst.date.getMonth() + 1).padStart(2, "0")}-${String(bookingState.dateTimeFirst.date.getDate()).padStart(2, "0")}`}
                    startTime={bookingState.dateTimeFirst.startTime}
                    endTime={bookingState.dateTimeFirst.endTime || ""}
                    allTimeRanges={bookingState.dateTimeFirst.allTimeRanges}
                    sessionType={getSessionType()}
                    serviceTheme={serviceTheme}
                  />
                </StepSection>
              )}
          </StepFrame>
        );

      // Step 3: Your Details
      case STEP_DETAILS:
        return (
          <StepFrame
            accentColor={accentColor}
            serviceTheme={serviceTheme}
            stepperIndex={2}
            onStepClick={handleStepperClick}
            footer={
              <BookingNavigation
                onPrevious={handlePrevious}
                onContinue={handlePersonalDetailsContinue}
                showPrevious={true}
                showContinue={true}
                continueDisabled={
                  !isDetailsStepComplete || isCreatingAppointment
                }
                continueLabel={
                  isCreatingAppointment ? "Creating Appointment..." : "Continue"
                }
                serviceTheme={serviceTheme}
              />
            }
          >
            <StepSection>
              <PersonalDetails
                onDataChange={handlePersonalDetailsChange}
                defaultData={bookingState.personalDetails || undefined}
                requireEmailVerification={REQUIRE_EMAIL_VERIFICATION}
                serviceTheme={serviceTheme}
                bookingSummary={fullSummary}
              />
            </StepSection>
          </StepFrame>
        );

      // Step 4: Payment
      case STEP_PAYMENT:
        return (
          <StepFrame
            accentColor={accentColor}
            serviceTheme={serviceTheme}
            stepperIndex={3}
            onStepClick={handleStepperClick}
            footer={
              <BookingNavigation
                onPrevious={handlePrevious}
                showPrevious={true}
                showContinue={false}
                continueLabel="Complete Booking"
                serviceTheme={serviceTheme}
              />
            }
          >
            <StepSection>
              <Payment
                email={bookingState.personalDetails?.email}
                onPaymentSubmit={handlePaymentSubmit}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                appointmentId={bookingState.appointmentId || undefined}
                serviceTheme={serviceTheme}
                bookingSummary={fullSummary}
              />
            </StepSection>
          </StepFrame>
        );

      case STEP_CONFIRMATION:
      default:
        return (
          <StepFrame
            accentColor={accentColor}
            serviceTheme={serviceTheme}
            stepperIndex={TOTAL_STEPS - 1}
            allCompleted
            footer={
              <BookingNavigation
                showPrevious={false}
                continueLabel="Go to Homepage"
                onContinue={() => {
                  window.location.href = "/";
                }}
                serviceTheme={serviceTheme}
              />
            }
          >
            <StepSection>
              <BookingConfirmation
                practitionerName={bookingState.practitionerFiltered?.name}
                practitionerRole={bookingState.practitionerFiltered?.role}
                supportOption={bookingState.supportOption?.name}
                duration={bookingState.supportOption?.duration}
                price={bookingState.supportOption?.price}
                appointmentDate={
                  bookingState.dateTimeFirst?.date
                    ? new Date(bookingState.dateTimeFirst.date)
                    : undefined
                }
                appointmentTime={bookingState.dateTimeFirst?.startTime}
              />
            </StepSection>
          </StepFrame>
        );
    }
  };

  return (
    <main className="min-h-screen bg-background pb-10 pt-24 md:pb-[60px] md:pt-[100px]">
      {/* Container: max-width 1579px, centered, flex column, responsive gap */}
      <div className="mx-auto flex w-full max-w-[1579px] flex-col items-center gap-8 px-4 md:gap-[30px] md:px-10">
        {renderStepContent()}
      </div>
    </main>
  );
}

// Main export wraps BookingPageContent in Suspense for useSearchParams
export default function Booking2Page() {
  return (
    <Suspense
      fallback={
        <main className="min-h-screen bg-background pb-10 pt-24 md:pb-[60px] md:pt-[100px]">
          <div className="mx-auto flex w-full max-w-[1579px] flex-col items-center gap-8 px-4 md:gap-[30px] md:px-10">
            <h1 className="sr-only">Book Your Session</h1>
            <div className="flex h-[400px] w-full items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-v3-primary border-t-transparent" />
            </div>
          </div>
        </main>
      }
    >
      <BookingPageContent />
    </Suspense>
  );
}
